/**
 * =============================================================================
 * Hook: useReferralReferralsAdmin
 * Versão: 1.0.0
 *
 * Descrição:
 *   Hook server-side para listagem paginada de **indicações** (leads/funil)
 *   na visão administrativa do tenant. Usa a RPC `get_referrals_paginated`
 *   criada na Etapa 1 do módulo Indicações Centralizadas.
 *
 *   - Paginação client-side a partir do JSONB devolvido pela RPC.
 *   - Filtros: programa, campanha, afiliado, status, período, busca textual.
 *   - KPIs (total, pendentes, convertidas, perdidas) calculados a partir das
 *     linhas da página atual + total do servidor (nas próximas iterações,
 *     evolveremos para KPIs globais via window function).
 *
 * RELEASE:
 *   2026-04-26 v1.0.0 - Criação inicial.
 * =============================================================================
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useReferralReferralsAdmin');

/** Status possíveis (espelho do enum `referral_status` do banco). */
export type ReferralAdminStatus =
  | 'pending'
  | 'qualified'
  | 'converted'
  | 'lost';

/** Linha da listagem administrativa de indicações. */
export interface AdminReferralRow {
  id: string;
  status: ReferralAdminStatus;
  lead_name: string | null;
  lead_email: string | null;
  lead_phone: string | null;
  lead_source: string | null;
  conversion_value: number | null;
  created_at: string;
  qualified_at: string | null;
  converted_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  // Origem
  affiliate_id: string;
  affiliate_name: string | null;
  affiliate_email: string | null;
  program_id: string;
  program_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  // Vínculos com CRM (criados pela trigger / processamento)
  contact_id: string | null;
  contact_name: string | null;
  deal_id: string | null;
  deal_title: string | null;
  // v1.1.0 — campos do negócio vinculado (vêm da RPC get_referrals_paginated v1.1.0)
  deal_status: 'open' | 'won' | 'lost' | null;
  deal_phase_name: string | null;
  deal_phase_color: string | null;
  deal_seller_name: string | null;
  deal_seller_avatar: string | null;
  deal_category_name: string | null;
  // v1.2.0 — campos para suportar a barra de progresso da fase
  // (componente <DealPhaseProgress /> reutilizado da listagem de Negócios)
  deal_pipeline_id: string | null;
  deal_phase_sort_order: number | null;
  deal_pipeline_total_phases: number | null;
  metadata: Record<string, any> | null;
}

export interface AdminReferralFilters {
  search?: string | null;
  program_id?: string | null;
  campaign_id?: string | null;
  affiliate_id?: string | null;
  status?: ReferralAdminStatus | null;
  date_from?: string | null;
  date_to?: string | null;
}

export interface AdminReferralKpis {
  total: number;
  pending: number;
  converted: number;
  lost: number;
  conversionRate: number; // 0..100
  totalRevenue: number;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

const EMPTY_KPIS: AdminReferralKpis = {
  total: 0,
  pending: 0,
  converted: 0,
  lost: 0,
  conversionRate: 0,
  totalRevenue: 0,
};

export function useReferralReferralsAdmin(
  filters: AdminReferralFilters | undefined,
  pagination: PaginationParams,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const offset = (pagination.page - 1) * pagination.pageSize;

  const query = useQuery({
    queryKey: [
      'referral-referrals-admin',
      currentTenant?.id,
      filters,
      pagination,
    ],
    queryFn: async () => {
      logger.debug('Buscando indicações (admin)', { filters, pagination });

      const { data, error } = await (supabase.rpc as any)(
        'get_referrals_paginated',
        {
          p_search: filters?.search || null,
          p_program_id: filters?.program_id || null,
          p_campaign_id: filters?.campaign_id || null,
          p_affiliate_id: filters?.affiliate_id || null,
          p_status: filters?.status || null,
          p_date_from: filters?.date_from || null,
          p_date_to: filters?.date_to || null,
          p_limit: pagination.pageSize,
          p_offset: offset,
          p_tenant_hint: currentTenant?.id || null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar indicações', error);
        throw error;
      }

      const rows = (data?.rows || []) as AdminReferralRow[];
      const total = Number(data?.total ?? 0);

      // KPIs locais (página atual). Numa iteração futura, mover para a RPC.
      const converted = rows.filter((r) => r.status === 'converted').length;
      const pending = rows.filter((r) => r.status === 'pending').length;
      const lost = rows.filter((r) => r.status === 'lost').length;
      const totalRevenue = rows
        .filter((r) => r.status === 'converted')
        .reduce((sum, r) => sum + Number(r.conversion_value ?? 0), 0);

      const kpis: AdminReferralKpis = {
        total,
        pending,
        converted,
        lost,
        conversionRate: total > 0 ? (converted / total) * 100 : 0,
        totalRevenue,
      };

      return {
        rows,
        totalCount: total,
        totalPages: Math.ceil(total / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    referrals: query.data?.rows || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || EMPTY_KPIS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['referral-referrals-admin', currentTenant?.id],
      });
    },
  };
}
